import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import * as z from "zod";

import AdminRoutes from "Routes/Components/Admin";

import { columns as seletivasColumns } from "Pages/Admin/Seletivas/ListSeletivas/columns";

import { HTTPErrorCallback } from "Helpers/Error";
import { cpfCnpjMask } from "Helpers/Format";
import { clearMask } from "Helpers/Util";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Button from "Components/Admin/Buttons";
import ContainerWithTabs from "Components/Admin/ContainerWithTabs";
import FormWrapper from "Components/Admin/Form/FormWrapper";
import Input from "Components/Admin/Form/Input";
import Loading from "Components/Admin/Loading";
import Section from "Components/Admin/Section";
import Table from "Components/Admin/Table";
import GetIcon from "Components/Global/Icons";

import UserModal from "./UserModal";
import { usersColumns } from "./columns";
import * as S from "./styles";

// Dentro do detalhe do patrocinador, a coluna "Patrocinador" é redundante.
const sponsorSeletivasColumns = seletivasColumns.filter(
  (column) => column.accessor !== "sponsor_name",
);

const DetailSponsor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [sponsor, setSponsor] = useState(null);
  const [seletivas, setSeletivas] = useState([]);
  const [fetchingData, setFetchingData] = useState(true);
  const [modal, setModal] = useState({ open: false, user: null });

  // --- Dados da empresa ---
  const [cnpj, setCnpj] = useState("");
  // Campos com máscara são controlados (o Input mascarado lê o estado via prop
  // `value`, não pelo setValue do react-hook-form).
  const [phone, setPhone] = useState("");

  const companyFormSchema = z.object({
    name: z.string().min(3, "Nome deve ter ao menos 3 caracteres"),
    email: z.string().email("E-mail inválido").optional().or(z.literal("")),
    phone: z.string().optional().or(z.literal("")),
    website: z.string().optional().or(z.literal("")),
    description: z.string().optional().or(z.literal("")),
  });

  const companyMethods = useForm({ resolver: zodResolver(companyFormSchema) });
  const { register: registerCompany, setValue: setCompanyValue } =
    companyMethods;

  // ---

  useEffect(() => {
    if (id) {
      getSponsor(id);
      getSeletivas(id);
    }
    // eslint-disable-next-line
  }, [id]);

  const getSeletivas = (sponsorId) => {
    api
      .post("/select-projects/list", { filters: { sponsor_id: sponsorId } })
      .then((response) => setSeletivas(response.data.select_projects || []))
      .catch((error) => HTTPErrorCallback(error));
  };

  const getSponsor = (sponsorId) => {
    setFetchingData(true);
    api
      .post("/sponsors/get-by-id", { sponsor_id: sponsorId })
      .then((response) => {
        const data = response.data.sponsor;
        setSponsor(data);
        setCompanyValue("name", data.name);
        setCompanyValue("email", data.email || "");
        setCompanyValue("website", data.website || "");
        setCompanyValue("description", data.description || "");
        setCnpj(cpfCnpjMask(data.cnpj || ""));
        setPhone(data.phone || "");
      })
      .catch((error) => HTTPErrorCallback(error))
      .finally(() => setFetchingData(false));
  };

  const submitCompany = (data) => {
    setLoading(true);
    data.cnpj = clearMask(cnpj);
    data.phone = phone ? clearMask(phone) : "";

    api
      .post("/sponsors/update", { sponsor_id: id, sponsor: data })
      .then(() => {
        setLoading(false);
        showNotification(
          "success",
          "Sucesso!",
          "Empresa atualizada com sucesso!",
        );
        getSponsor(id);
      })
      .catch((error) => {
        setLoading(false);
        HTTPErrorCallback(error);
      });
  };

  if (fetchingData) return <Loading />;

  return (
    <Section>
      <S.Wrapper>
        {/* Botão voltar */}
        <S.Buttons>
          <Button
            type="button"
            text="Voltar"
            onClick={() => navigate(AdminRoutes.ListSponsors.path)}
            width="150px"
          />
          {/* {sponsor && (
            <ToggleStatus
              route="/sponsors/toggle-status/"
              item={sponsor}
              refresh={() => getSponsor(id)}
            />
          )} */}
        </S.Buttons>

        {/* Dados da Empresa */}
        <S.Card>
          <S.CardTitle>Dados da Empresa</S.CardTitle>
          <FormWrapper submit={submitCompany} {...companyMethods}>
            <S.FormGrid>
              <Input
                {...registerCompany("name")}
                type="text"
                label="Nome da Empresa:"
                placeholder="Razão Social"
                required
              />
              <Input
                name="cnpj"
                type="text"
                label="CNPJ:"
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onChange={(e) => setCnpj(cpfCnpjMask(e.target.value))}
                required
              />
              <Input
                {...registerCompany("email")}
                type="email"
                label="E-mail Corporativo:"
                placeholder="contato@empresa.com"
              />
              <Input
                name="phone"
                type="text"
                label="Telefone:"
                placeholder="+55 (00) 00000-0000"
                mask="+99 (99) 99999-9999"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
              <Input
                {...registerCompany("website")}
                type="text"
                label="Website:"
                placeholder="https://empresa.com.br"
              />
              <Input
                {...registerCompany("description")}
                type="text"
                label="Descrição:"
                placeholder="Breve descrição da empresa"
              />
            </S.FormGrid>
            <Button
              type="submit"
              text={loading ? "Salvando..." : "Salvar Alterações"}
              disabled={loading}
              width="200px"
            />
          </FormWrapper>
        </S.Card>

        {/* Abas: Usuários vinculados (padrão) e Seletivas */}
        <ContainerWithTabs
          mt="20px"
          tabs={[
            {
              title: `Usuários Vinculados (${sponsor?.users?.length || 0})`,
              component: (
                <>
                  <S.Buttons>
                    <div />
                    <Button
                      type="button"
                      text="Adicionar Usuário"
                      onClick={() => setModal({ open: true, user: null })}
                      width="200px"
                    />
                  </S.Buttons>
                  <S.UsersTableWrapper>
                    <Table
                      key="sponsor-users-table"
                      name="Usuários"
                      data={sponsor?.users || []}
                      columns={[
                        ...usersColumns,
                        {
                          Header: "Ações",
                          accessor: "actions",
                          noSearch: true,
                          width: "70px",
                          Cell: ({ cell }) => {
                            const row = cell.row.original;
                            return (
                              <S.Actions>
                                <GetIcon
                                  name="Edit"
                                  onClick={() =>
                                    setModal({ open: true, user: row })
                                  }
                                />
                              </S.Actions>
                            );
                          },
                        },
                      ]}
                    />
                  </S.UsersTableWrapper>
                </>
              ),
            },
            {
              title: `Seletivas (${seletivas.length})`,
              component: (
                <>
                  <S.Buttons>
                    <div />
                    <Button
                      type="button"
                      text="Cadastrar Seletiva"
                      onClick={() =>
                        navigate(
                          `${AdminRoutes.CreateSeletiva.path}?sponsor_id=${id}`,
                        )
                      }
                      width="200px"
                    />
                  </S.Buttons>
                  <S.UsersTableWrapper>
                    <Table
                      key="sponsor-seletivas-table"
                      name="Seletivas"
                      data={seletivas}
                      columns={[
                        ...sponsorSeletivasColumns,
                        {
                          Header: "Ações",
                          accessor: "actions",
                          noSearch: true,
                          width: "70px",
                          Cell: ({ cell }) => {
                            const row = cell.row.original;
                            return (
                              <S.Actions>
                                <GetIcon
                                  name="Edit"
                                  onClick={() =>
                                    navigate(
                                      AdminRoutes.UpdateSeletiva.path.replace(
                                        ":id",
                                        row.id,
                                      ),
                                    )
                                  }
                                />
                              </S.Actions>
                            );
                          },
                        },
                      ]}
                    />
                  </S.UsersTableWrapper>
                </>
              ),
            },
          ]}
        />

        {modal.open && (
          <UserModal
            user={modal.user}
            sponsorId={id}
            onClose={() => setModal({ open: false, user: null })}
            onSuccess={() => getSponsor(id)}
          />
        )}
      </S.Wrapper>
    </Section>
  );
};

export default DetailSponsor;
